import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import {
  Expense,
  ExpenseFilters,
  ExpenseListResponse,
  ExpenseSummary,
  ExpenseStatus,
  ExpenseCategory
} from '@/types/expense';

// State interface
export interface ExpenseState {
  // Data
  expenses: Expense[];
  summary: ExpenseSummary | null;
  selectedExpense: Expense | null;
  
  // UI State
  isLoading: boolean;
  error: string | null;
  
  // List state
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
  
  // Filters
  filters: ExpenseFilters;
  
  // Modal states
  showCreateModal: boolean;
  showEditModal: boolean;
  showDetailsModal: boolean;
}

// Action types
export type ExpenseAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_EXPENSES'; payload: ExpenseListResponse }
  | { type: 'SET_SUMMARY'; payload: ExpenseSummary }
  | { type: 'SELECT_EXPENSE'; payload: Expense | null }
  | { type: 'ADD_EXPENSE'; payload: Expense }
  | { type: 'UPDATE_EXPENSE'; payload: Expense }
  | { type: 'REMOVE_EXPENSE'; payload: string }
  | { type: 'SET_FILTERS'; payload: Partial<ExpenseFilters> }
  | { type: 'RESET_FILTERS' }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SHOW_CREATE_MODAL'; payload: boolean }
  | { type: 'SHOW_EDIT_MODAL'; payload: boolean }
  | { type: 'SHOW_DETAILS_MODAL'; payload: boolean }
  | { type: 'REFRESH_EXPENSE'; payload: Expense };

// Initial state
const initialState: ExpenseState = {
  expenses: [],
  summary: null,
  selectedExpense: null,
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  hasNext: false,
  hasPrevious: false,
  filters: {},
  showCreateModal: false,
  showEditModal: false,
  showDetailsModal: false,
};

// Reducer function
function expenseReducer(state: ExpenseState, action: ExpenseAction): ExpenseState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error, // Clear error when loading starts
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'SET_EXPENSES':
      return {
        ...state,
        expenses: action.payload.expenses,
        currentPage: action.payload.page,
        totalPages: action.payload.totalPages,
        totalCount: action.payload.totalCount,
        hasNext: action.payload.hasNext,
        hasPrevious: action.payload.hasPrevious,
        isLoading: false,
        error: null,
      };

    case 'SET_SUMMARY':
      return {
        ...state,
        summary: action.payload,
        isLoading: false,
        error: null,
      };

    case 'SELECT_EXPENSE':
      return {
        ...state,
        selectedExpense: action.payload,
      };

    case 'ADD_EXPENSE':
      return {
        ...state,
        expenses: [action.payload, ...state.expenses],
        totalCount: state.totalCount + 1,
      };

    case 'UPDATE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.map(expense =>
          expense.id === action.payload.id ? action.payload : expense
        ),
        selectedExpense: state.selectedExpense?.id === action.payload.id 
          ? action.payload 
          : state.selectedExpense,
      };

    case 'REFRESH_EXPENSE':
      // Update both the list and selected expense if it matches
      const updatedExpenses = state.expenses.map(expense =>
        expense.id === action.payload.id ? action.payload : expense
      );
      return {
        ...state,
        expenses: updatedExpenses,
        selectedExpense: state.selectedExpense?.id === action.payload.id 
          ? action.payload 
          : state.selectedExpense,
      };

    case 'REMOVE_EXPENSE':
      return {
        ...state,
        expenses: state.expenses.filter(expense => expense.id !== action.payload),
        totalCount: Math.max(0, state.totalCount - 1),
        selectedExpense: state.selectedExpense?.id === action.payload 
          ? null 
          : state.selectedExpense,
      };

    case 'SET_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        currentPage: 1, // Reset to first page when filters change
      };

    case 'RESET_FILTERS':
      return {
        ...state,
        filters: {},
        currentPage: 1,
      };

    case 'SET_PAGE':
      return {
        ...state,
        currentPage: action.payload,
      };

    case 'SHOW_CREATE_MODAL':
      return {
        ...state,
        showCreateModal: action.payload,
        selectedExpense: action.payload ? null : state.selectedExpense, // Clear selection when opening create modal
      };

    case 'SHOW_EDIT_MODAL':
      return {
        ...state,
        showEditModal: action.payload,
      };

    case 'SHOW_DETAILS_MODAL':
      return {
        ...state,
        showDetailsModal: action.payload,
      };

    default:
      return state;
  }
}

// Context interface
interface ExpenseContextType {
  state: ExpenseState;
  dispatch: React.Dispatch<ExpenseAction>;
  
  // Convenience methods
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setExpenses: (data: ExpenseListResponse) => void;
  setSummary: (summary: ExpenseSummary) => void;
  selectExpense: (expense: Expense | null) => void;
  addExpense: (expense: Expense) => void;
  updateExpense: (expense: Expense) => void;
  refreshExpense: (expense: Expense) => void;
  removeExpense: (expenseId: string) => void;
  setFilters: (filters: Partial<ExpenseFilters>) => void;
  resetFilters: () => void;
  setPage: (page: number) => void;
  
  // Modal methods
  openCreateModal: () => void;
  closeCreateModal: () => void;
  openEditModal: (expense: Expense) => void;
  closeEditModal: () => void;
  openDetailsModal: (expense: Expense) => void;
  closeDetailsModal: () => void;
  
  // Computed properties
  hasActiveFilters: boolean;
  filteredStatusCount: number;
  filteredCategoryCount: number;
}

// Create context
const ExpenseContext = createContext<ExpenseContextType | undefined>(undefined);

// Provider component
interface ExpenseProviderProps {
  children: ReactNode;
}

export const ExpenseProvider: React.FC<ExpenseProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(expenseReducer, initialState);

  // Convenience methods
  const setLoading = (loading: boolean) => dispatch({ type: 'SET_LOADING', payload: loading });
  const setError = (error: string | null) => dispatch({ type: 'SET_ERROR', payload: error });
  const setExpenses = (data: ExpenseListResponse) => dispatch({ type: 'SET_EXPENSES', payload: data });
  const setSummary = (summary: ExpenseSummary) => dispatch({ type: 'SET_SUMMARY', payload: summary });
  const selectExpense = (expense: Expense | null) => dispatch({ type: 'SELECT_EXPENSE', payload: expense });
  const addExpense = (expense: Expense) => dispatch({ type: 'ADD_EXPENSE', payload: expense });
  const updateExpense = (expense: Expense) => dispatch({ type: 'UPDATE_EXPENSE', payload: expense });
  const refreshExpense = (expense: Expense) => dispatch({ type: 'REFRESH_EXPENSE', payload: expense });
  const removeExpense = (expenseId: string) => dispatch({ type: 'REMOVE_EXPENSE', payload: expenseId });
  const setFilters = (filters: Partial<ExpenseFilters>) => dispatch({ type: 'SET_FILTERS', payload: filters });
  const resetFilters = () => dispatch({ type: 'RESET_FILTERS' });
  const setPage = (page: number) => dispatch({ type: 'SET_PAGE', payload: page });

  // Modal methods
  const openCreateModal = () => dispatch({ type: 'SHOW_CREATE_MODAL', payload: true });
  const closeCreateModal = () => dispatch({ type: 'SHOW_CREATE_MODAL', payload: false });
  const openEditModal = (expense: Expense) => {
    dispatch({ type: 'SELECT_EXPENSE', payload: expense });
    dispatch({ type: 'SHOW_EDIT_MODAL', payload: true });
  };
  const closeEditModal = () => dispatch({ type: 'SHOW_EDIT_MODAL', payload: false });
  const openDetailsModal = (expense: Expense) => {
    dispatch({ type: 'SELECT_EXPENSE', payload: expense });
    dispatch({ type: 'SHOW_DETAILS_MODAL', payload: true });
  };
  const closeDetailsModal = () => dispatch({ type: 'SHOW_DETAILS_MODAL', payload: false });

  // Computed properties
  const hasActiveFilters = Object.keys(state.filters).some(key => {
    const value = state.filters[key as keyof ExpenseFilters];
    return Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '';
  });

  const filteredStatusCount = state.filters.status?.length || 0;
  const filteredCategoryCount = state.filters.category?.length || 0;

  const contextValue: ExpenseContextType = {
    state,
    dispatch,
    setLoading,
    setError,
    setExpenses,
    setSummary,
    selectExpense,
    addExpense,
    updateExpense,
    refreshExpense,
    removeExpense,
    setFilters,
    resetFilters,
    setPage,
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDetailsModal,
    closeDetailsModal,
    hasActiveFilters,
    filteredStatusCount,
    filteredCategoryCount,
  };

  return (
    <ExpenseContext.Provider value={contextValue}>
      {children}
    </ExpenseContext.Provider>
  );
};

// Custom hook to use the expense context
export const useExpenseContext = () => {
  const context = useContext(ExpenseContext);
  if (context === undefined) {
    throw new Error('useExpenseContext must be used within an ExpenseProvider');
  }
  return context;
};

export default ExpenseContext; 